import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import PrioritizeDetailModal, {
  PrioritizeItem,
  FeatureStatus,
  HealthState,
} from '../../components/PrioritizeDetailModal';
import { prioritizeService } from '../../services/prioritizeService';
import { organizationService, OrgMember } from '../../services/organizationService';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useActiveProject } from '../../hooks/useActiveProject';
import { useFeatureLinkData } from '../../hooks/useFeatureLinkData';
import '../../styles/Pages.css';
import '../../styles/CustomersList.css';
import '../../styles/FeaturesPage.css';

/* ── Board columns (match the design) ─────────────────────────── */
const COLUMNS: { key: FeatureStatus; label: string; flag: string }[] = [
  { key: 'new-idea',    label: 'New idea',    flag: '#14b8a6' },
  { key: 'candidate',   label: 'Candidate',   flag: '#8b5cf6' },
  { key: 'planned',     label: 'Planned',     flag: '#3b82f6' },
  { key: 'in-progress', label: 'In progress', flag: '#f59e0b' },
  { key: 'released',    label: 'Released',    flag: '#22c55e' },
];

const HEALTH_STYLE: Record<HealthState, { color: string; label: string }> = {
  'on-track':  { color: '#10B981', label: 'On track' },
  'at-risk':   { color: '#F97316', label: 'At risk' },
  'off-track': { color: '#EF4444', label: 'Off track' },
  'none':      { color: '#9ca3af', label: '—' },
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const formatDateRange = (start?: string, end?: string): string => {
  const fmt = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const s = fmt(start), e = fmt(end);
  if (s && e) return `${s} → ${e}`;
  return s || e;
};

const initialsOf = (name?: string | null) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

/* ── Avatar ───────────────────────────────────────────────────── */
const Avatar: React.FC<{ text: string; bg: string }> = ({ text, bg }) => (
  <span className="fpg-avatar" style={{ background: bg }}>{text}</span>
);

/* ── Feature card ─────────────────────────────────────────────── */
const FeatureCard: React.FC<{
  item: PrioritizeItem;
  ownerName?: string | null;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
}> = ({ item, ownerName, onClick, onDragStart }) => {
  const health = HEALTH_STYLE[item.health || 'none'];
  const showHealth = item.health && item.health !== 'none';
  const dateRange = formatDateRange(item.startDate, item.endDate);
  const pct = item.workProgress ?? 0;
  const teamInitials = item.teams ? initialsOf(item.teams.split(',')[0]) : null;

  return (
    <div className="fpg-card" draggable onDragStart={onDragStart} onClick={onClick}>
      <div className="fpg-card-title">
        <span className="fpg-card-icon" style={{ background: item.color || '#3B82F6' }} />
        <span className="fpg-card-name">{item.name}</span>
      </div>

      {item.objective ? (
        <div className="fpg-card-objective">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3" fill="#3b82f6" stroke="none"/></svg>
          <span>{item.objective}</span>
        </div>
      ) : (
        <div className="fpg-card-objective fpg-muted">…</div>
      )}

      <div className="fpg-card-tag">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
        <span className="fpg-muted">{item.sampleTag || '-'}</span>
      </div>

      <div className="fpg-card-meta">
        {showHealth && (
          <span className="fpg-health">
            <span className="fpg-health-dot" style={{ background: health.color }} />
            {health.label}
          </span>
        )}
        {dateRange && (
          <span className="fpg-date">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {dateRange}
          </span>
        )}
        <button type="button" className="fpg-card-more" onClick={e => e.stopPropagation()}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>
        </button>
      </div>

      <div className="fpg-card-footer">
        <span className="fpg-progress">
          <span className="fpg-progress-track"><span className="fpg-progress-fill" style={{ width: `${pct}%` }} /></span>
          <span className="fpg-progress-num">{pct}%</span>
        </span>
        <span className="fpg-card-avatars">
          <Avatar text={initialsOf(ownerName)} bg="#EC4899" />
          {teamInitials && <Avatar text={teamInitials} bg="#64748b" />}
        </span>
      </div>
    </div>
  );
};

/* ── Page ─────────────────────────────────────────────────────── */
const Features: React.FC = () => {
  const { currentOrg } = useAuth();
  const { can } = usePermissions();
  const canManage = can('product_features');
  const { activeProjectId } = useActiveProject();
  const { companies, customerUsers, documents } = useFeatureLinkData(currentOrg?.id, activeProjectId);

  const [items, setItems] = useState<PrioritizeItem[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<PrioritizeItem | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<FeatureStatus | null>(null);

  /* Load feature items + members */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentOrg?.id) return;
      try {
        const rows = await prioritizeService.getItems(activeProjectId);
        if (!cancelled) setItems(rows);
      } catch (err) {
        console.error('Failed to load features:', err);
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id, activeProjectId]);

  useEffect(() => {
    if (!currentOrg?.id) return;
    organizationService.getMembersWithProfiles(currentOrg.id).then(setMembers).catch(() => {});
  }, [currentOrg?.id]);

  const memberName = (id?: string | null) => {
    if (!id) return null;
    const m = members.find(x => x.user_id === id);
    return m ? (m.full_name || m.email) : null;
  };

  const q = search.trim().toLowerCase();
  const features = useMemo(
    () => items.filter(i => i.kind === 'feature' && (!q || i.name.toLowerCase().includes(q))),
    [items, q],
  );

  // Group features into the 5 columns; unmapped statuses fall into "New idea".
  const columnKeys = new Set(COLUMNS.map(c => c.key));
  const byColumn = (col: FeatureStatus) =>
    features.filter(f => {
      const s = (f.status as FeatureStatus) || 'new-idea';
      return columnKeys.has(s) ? s === col : col === 'new-idea';
    });

  const parentCandidates = useMemo(
    () => items.filter(i => i.kind === 'product' || i.kind === 'epic'),
    [items],
  );

  /* Save (edit) */
  const handleSave = (updated: PrioritizeItem) => {
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    setDraft(null);
    prioritizeService.updateItem(updated.id, updated).catch(err => {
      console.error('Failed to save feature:', err);
      alert(`Failed to save: ${err?.message ?? err}`);
    });
  };

  const handleDelete = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    setDraft(null);
    prioritizeService.deleteItems([id]).catch(err => {
      console.error('Failed to delete feature:', err);
      alert(`Failed to delete: ${err?.message ?? err}`);
    });
  };

  /* Add feature */
  const handleAddFeature = async () => {
    const newItem: PrioritizeItem = {
      id: 'temp_' + uid(),
      kind: 'feature',
      name: 'New Feature',
      ownerId: null,
      importanceScore: 0,
      color: 'var(--brand-500)',
      health: 'none',
      status: 'new-idea',
      workProgress: 0,
      parentId: null,
      productParentId: null,
      createdAt: new Date().toISOString(),
    };
    try {
      const saved = await prioritizeService.createItem(newItem, items.length);
      setItems(prev => [...prev, saved]);
      setDraft(saved);
    } catch (err) {
      console.error('Failed to create feature:', err);
      alert('Failed to create feature. Please try again.');
    }
  };

  /* Drag-drop between columns → change status */
  const handleDropOn = (status: FeatureStatus) => {
    setDragOverCol(null);
    if (!dragId) return;
    const dragged = items.find(i => i.id === dragId);
    setDragId(null);
    if (!dragged || dragged.status === status) return;
    const updated = { ...dragged, status };
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    prioritizeService.updateItem(updated.id, updated).catch(err => {
      console.error('Failed to move feature:', err);
      alert(`Failed to move feature: ${err?.message ?? err}`);
    });
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <h1 className="customers-title">Features</h1>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search features…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
            {canManage && (
              <button className="customers-add-btn btn-primary-cta" onClick={handleAddFeature} title="Add a new feature">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Feature
              </button>
            )}
          </div>
        </div>

        <div className="fpg-board">
          {COLUMNS.map(col => {
            const cards = byColumn(col.key);
            return (
              <div
                key={col.key}
                className={`fpg-column ${dragOverCol === col.key ? 'fpg-column-over' : ''}`}
                onDragOver={e => { if (dragId) { e.preventDefault(); setDragOverCol(col.key); } }}
                onDragLeave={() => setDragOverCol(prev => prev === col.key ? null : prev)}
                onDrop={() => handleDropOn(col.key)}
              >
                <div className="fpg-column-header">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={col.flag} stroke="none"><path d="M5 3v18l7-5 7 5V3z"/></svg>
                  <span className="fpg-column-title">{col.label}</span>
                  <span className="fpg-column-count">{cards.length}</span>
                </div>
                <div className="fpg-column-body">
                  {cards.map(item => (
                    <FeatureCard
                      key={item.id}
                      item={item}
                      ownerName={memberName(item.ownerId)}
                      onClick={() => setDraft(item)}
                      onDragStart={() => setDragId(item.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {draft && (
          <PrioritizeDetailModal
            item={draft}
            parentCandidates={parentCandidates}
            members={members}
            allItems={items}
            companies={companies}
            customerUsers={customerUsers}
            documents={documents}
            onDelete={handleDelete}
            onClose={() => setDraft(null)}
            onSave={handleSave}
          />
        )}
      </div>
    </Layout>
  );
};

export default Features;
